import { useCallback, useEffect, useState, type ReactNode } from "react";
import { TRACK_EXPIRED } from "../lib/training";
import { ArrowLeft, Check, LoaderCircle, RotateCw } from "lucide-react";
import { Badge, Button } from "@codelittinc/carbon-design-system";
import { sanitizeRichText } from "@codelittinc/carbon-design-system/utils";
import type { TrainingItem, TrainingProcessResponse } from "@carbon-os/server/contract";
import { trackApp, trackTraining, type TrainingEvents } from "../lib/analytics";
import { sessionFetch } from "../lib/session";

type ReaderState = { kind: "loading" } | { kind: "ready"; document: TrainingProcessResponse } | { kind: "failed"; message: string };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

async function readProcess(id: string): Promise<TrainingProcessResponse> {
  const response = await sessionFetch(`/api/training/processes/${encodeURIComponent(id)}`, { cache: "no-store", headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(response.status === 404 ? "This process is no longer available." : "We couldn't load this process. Try again.");
  const body = await response.json() as TrainingProcessResponse;
  if (body.processId !== id || !UUID.test(body.versionId) || typeof body.bodyHtml !== "string") throw new Error("This process response could not be read.");
  return body;
}

/** A process's sanitized body; a click on a handbook link goes to `onLink`. Shared with the Handbook page. */
export function ProcessBody({ html, onLink }: { html: string | null; onLink: (id: string) => void }) {
  if (!html) return <p className="text-sm text-text-muted">This process has no readable content.</p>;
  const prepared = html.replace(/href=(["'])\/handbook\/([0-9a-f-]{36})\1/gi,
    (_match, quote: string, id: string) => `href=${quote}https://handbook-link.invalid/doc_${id}${quote}`);
  const clean = sanitizeRichText(prepared, { formatting: "extended", images: true }).replace(
    /href=["']https:\/\/handbook-link\.invalid\/doc_([0-9a-f-]{36})["']/gi,
    (_match, id: string) => `href="/handbook/${id}"`,
  );
  return <div className="space-y-3 text-sm leading-relaxed text-text-secondary [&_h1]:mt-6 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:text-text-primary [&_h2]:mt-6 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-text-primary [&_h3]:mt-4 [&_h3]:font-semibold [&_h3]:text-text-primary [&_strong]:font-semibold [&_strong]:text-text-primary [&_li]:my-1 [&_a]:text-accent-text [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_p]:my-2 [&_img]:block [&_img]:mx-auto [&_img]:max-w-full [&_img]:h-auto" onClick={(event) => {
    const anchor = (event.target as HTMLElement).closest("a");
    const match = anchor?.getAttribute("href")?.match(/^\/handbook\/([0-9a-f-]{36})$/i);
    if (match && match[1] && UUID.test(match[1])) { event.preventDefault(); onLink(match[1]); }
  }} dangerouslySetInnerHTML={{ __html: clean }} />;
}

/**
 * "What changed since version N": the authors' summaries, and the changed
 * passages behind a disclosure, never required reading. Nothing when there is
 * no earlier version to compare with. Shared with the Handbook page.
 */
export function ProcessChanges({ document, children }: { document: TrainingProcessResponse; children?: ReactNode }) {
  if (document.previousVersion === null) return null;
  return <section aria-labelledby="changes-title" className="rounded-lg border border-border bg-surface p-5"><h2 id="changes-title" className="font-medium text-text-primary">What changed since version {document.previousVersion}?</h2>{children}<ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-text-secondary">{document.changeSummaries.map((change) => change.summary && <li key={change.version}><span className="font-medium">Version {change.version}:</span> {change.summary}</li>)}</ul>{document.changes && <details className="mt-3"><summary className="cursor-pointer text-sm text-accent-text">Show changed passages</summary><p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed">{document.changes.map((part, index) => part.type === "insert" ? <ins key={index} className="text-success-text">{part.text}</ins> : part.type === "delete" ? <del key={index} className="text-error-text">{part.text}</del> : <span key={index}>{part.text}</span>)}</p></details>}</section>;
}

/**
 * A handbook process as a step of the track player: the current version, what
 * changed since the version they last read, and "I have read this version".
 * A handbook link in it opens that document here, read-only, with a way back.
 * `onAcknowledged` runs with the acknowledgment's time once it is saved, or
 * with null when it was refused because the track ended (the list re-reads
 * and locks it).
 */
export function ProcessStep({ item, trackId, onAcknowledged }: { item: TrainingItem; trackId: string; onAcknowledged: (completedAt: string | null) => void }) {
  const processId = item.processId ?? item.materialId;
  const [linked, setLinked] = useState<string | null>(null);
  const [state, setState] = useState<ReaderState>({ kind: "loading" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentId = linked ?? processId;
  const isRequiredStep = linked === null;
  const needsAcknowledgment = state.kind === "ready" ? state.document.completedAt === null : item.needsReread === true;

  const load = useCallback(async (id: string) => {
    setState({ kind: "loading" });
    setError(null);
    try { setState({ kind: "ready", document: await readProcess(id) }); }
    catch (caught) { setState({ kind: "failed", message: caught instanceof Error ? caught.message : "We couldn't load this process." }); }
  }, []);

  useEffect(() => { void load(currentId); }, [currentId, load]);

  async function acknowledge() {
    if (state.kind !== "ready" || !isRequiredStep || !item.versionId) return;
    setSaving(true);
    setError(null);
    const response = await sessionFetch(`/api/training/processes/${encodeURIComponent(processId)}/acknowledgments`, {
      method: "PUT", credentials: "same-origin", headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ versionId: state.document.versionId }),
    }).catch(() => null);
    const answered = (outcome: TrainingEvents["training_process_acknowledged"]["outcome"]) =>
      trackTraining("training_process_acknowledged", { material_id: item.materialId, track_id: trackId, outcome });
    if (!response?.ok) {
      const body = await response?.json().catch(() => null) as { code?: string } | null;
      answered(body?.code === "track_expired" || body?.code === "version_changed" ? body.code : "error");
      if (body?.code === "track_expired") {
        setError(TRACK_EXPIRED);
        // The list re-reads, so this step locks and the button goes.
        onAcknowledged(null);
      } else if (body?.code === "version_changed") {
        // Loaded first: `load` clears the error, and this one must stay up.
        await load(processId);
        setError("A newer version was published while you were reading. Review the updated process before acknowledging it.");
      } else setError("We couldn't save your acknowledgment. Try again.");
      setSaving(false);
      return;
    }
    // Read on the spot: the button goes and "You read this version" shows, also
    // where there is no next step to move on to. Only a new version asks again.
    const saved = await response.json().catch(() => null) as { completedAt?: unknown } | null;
    const completedAt = typeof saved?.completedAt === "string" ? saved.completedAt : new Date().toISOString();
    answered("saved");
    setState((current) => current.kind === "ready" ? { kind: "ready", document: { ...current.document, completedAt } } : current);
    setSaving(false);
    onAcknowledged(completedAt);
  }

  return (
    <article aria-label="Process" className="space-y-6">
      {!isRequiredStep && <Button variant="ghost" size="sm" onClick={() => setLinked(null)}><ArrowLeft size={16} />Back to {item.title}</Button>}
      {state.kind === "loading" && <p role="status" className="text-sm text-text-muted"><LoaderCircle className="mr-2 inline size-4 animate-spin motion-reduce:animate-none" />Loading process…</p>}
      {state.kind === "failed" && <div className="space-y-3"><p role="alert" className="text-sm text-text-muted">{state.message}</p><Button variant="outline" onClick={() => { trackApp("retry_clicked", { area: "process" }); void load(currentId); }}><RotateCw size={14} />Try again</Button></div>}
      {state.kind === "ready" && <>
        <div className="flex flex-wrap items-center gap-2">
          {!isRequiredStep && <h2 className="text-lg font-semibold text-text-primary">{state.document.title}</h2>}
          <Badge variant="default">Version {state.document.version}</Badge>
          {state.document.completedAt && isRequiredStep && <span className="text-xs text-text-muted">You read this version on {new Date(state.document.completedAt).toLocaleDateString()}</span>}
        </div>
        {isRequiredStep && needsAcknowledgment && <ProcessChanges document={state.document} />}
        <ProcessBody html={state.document.bodyHtml} onLink={(id) => setLinked(id)} />
        {error && <p role="alert" className="text-sm text-error-text">{error}</p>}
        {isRequiredStep && needsAcknowledgment && item.locked && <p className="border-t border-border-subtle pt-5 text-sm text-text-secondary">This track has ended. You can still read this process, but it can't be acknowledged.</p>}
        {isRequiredStep && needsAcknowledgment && !item.locked && <div className="border-t border-border-subtle pt-5"><Button className="w-full sm:w-auto" disabled={saving} onClick={() => void acknowledge()}>{saving ? <><LoaderCircle className="size-4 animate-spin motion-reduce:animate-none" />Saving…</> : <><Check size={14} />I have read this version</>}</Button></div>}
      </>}
    </article>
  );
}
